/**
 * KPICardGrid Component
 * v4.10 - 2026-04-28 - TRUST-KPI-01: ConfidenceBadge per card (renders when answer_confidence present)
 * v3.50 - 2026-02-24 - Story 9F: Responsive KPI value font (clamp) to prevent clipping (ADR-002)
 * v3.29 - 2026-02-22 - Allow title + description to wrap (2 lines max), remove noWrap truncation
 * v2.45 - 2026-02-02 - Pre-computed KPI cards with sparklines, trends, and thresholds
 * v3.26 - 2026-02-19 - Added description caption, "vs previous" comparison, trend context chip
 *
 * Displays a responsive grid of KPI cards from Cosmos DB cache.
 * Features: sparklines with period labels, trend indicators, threshold alerts,
 * drag-to-reorder support, loading skeletons, error/empty/stale states,
 * description captions, previous value comparison, trend quality chips.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  AlertTitle,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  Payments as PaymentsIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import {
  dashboardKPIService,
  DashboardKPIsResponse,
  DashboardCardsResponse,
  KPIValue,
  KPICardDefinition
} from '../services/api';
// v4.10 - TRUST-KPI-01: per-card answer-confidence indicator
import ConfidenceBadge from './ConfidenceBadge';

// v2.45 - Icon mapping for MUI icons
const iconMap: Record<string, React.ReactElement> = {
  account_balance: <AccountBalanceIcon />,
  warning: <WarningIcon />,
  trending_up: <TrendingUpIcon />,
  people: <PeopleIcon />,
  bar_chart: <BarChartIcon />,
  payments: <PaymentsIcon />,
  analytics: <AnalyticsIcon />
};

// v2.45 - Get icon component from icon name
const getIcon = (iconName: string): React.ReactElement => {
  return iconMap[iconName] || <BarChartIcon />;
};

// v2.45 - Threshold status calculation
type ThresholdStatus = 'normal' | 'warning' | 'critical';

const getThresholdStatus = (
  value: number | null,
  thresholds: { warning: number; critical: number } | undefined,
  trendDirection: string
): ThresholdStatus => {
  if (!thresholds || value === null) return 'normal';

  if (trendDirection === 'down_is_good') {
    // Higher values are bad (e.g., Overdue AR)
    if (value >= thresholds.critical) return 'critical';
    if (value >= thresholds.warning) return 'warning';
  } else {
    // Lower values are bad (e.g., Revenue)
    if (value <= thresholds.critical) return 'critical';
    if (value <= thresholds.warning) return 'warning';
  }
  return 'normal';
};

// v2.45 - Card styling based on threshold status
const getCardStyle = (status: ThresholdStatus, accentColor: string) => ({
  borderLeft: `4px solid ${
    status === 'critical' ? '#F44336' :
    status === 'warning' ? '#FF9800' :
    accentColor
  }`,
  backgroundColor: status === 'critical' ? 'rgba(244, 67, 54, 0.05)' :
                   status === 'warning' ? 'rgba(255, 152, 0, 0.05)' :
                   'inherit'
});

// v4.03 - STORY-142: Format value based on format type; added duration + units label cases
const formatValue = (
  value: number | null,
  format: string,
  formatOptions?: { currency?: string; decimals?: number; abbreviate?: boolean; unit?: string },
  cardUnit?: string  // v4.03 - STORY-142: raw unit field from KPICardDefinition for "units" label
): string => {
  if (value === null) return '--';

  const decimals = formatOptions?.decimals ?? 0;
  const abbreviate = formatOptions?.abbreviate ?? false;

  // v4.03 - STORY-142: duration format — e.g. "12.4 days"
  if (format === 'duration') {
    const timeUnit = formatOptions?.unit || 'days';
    return `${value.toFixed(decimals)} ${timeUnit}`;
  }

  if (abbreviate && Math.abs(value) >= 1000) {
    if (Math.abs(value) >= 1000000) {
      value = value / 1000000;
      const suffix = 'M';
      if (format === 'currency') {
        return `$${value.toFixed(1)}${suffix}`;
      }
      return `${value.toFixed(1)}${suffix}`;
    } else if (Math.abs(value) >= 1000) {
      value = value / 1000;
      const suffix = 'K';
      if (format === 'currency') {
        return `$${value.toFixed(1)}${suffix}`;
      }
      return `${value.toFixed(1)}${suffix}`;
    }
  }

  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formatOptions?.currency || 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  }

  if (format === 'percentage') {
    return `${value.toFixed(decimals)}%`;
  }

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);

  // v4.03 - STORY-146: append unit label for "units" (e.g. "847 units"); "count" = plain number
  if (cardUnit === 'units') {
    return `${formatted} units`;
  }

  return formatted;
};

// v2.45 - Format relative time
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'less than an hour ago';
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'yesterday';
  return `${diffDays} days ago`;
};

// v2.45 - Loading skeleton component
const KPICardSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <Grid container spacing={3}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={12} sm={6} md={3} key={index}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ ml: 2, flex: 1 }}>
                <Skeleton variant="text" width="80%" />
              </Box>
            </Box>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="rectangular" height={40} sx={{ mt: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Skeleton variant="text" width="30%" />
              <Skeleton variant="text" width="30%" />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// v2.45 - Individual KPI Card component
interface KPICardProps {
  card: KPICardDefinition;
  kpiValue?: KPIValue;
  onClick?: () => void;
}

const KPICard: React.FC<KPICardProps> = ({ card, kpiValue, onClick }) => {
  const theme = useTheme();

  const thresholdStatus = getThresholdStatus(
    kpiValue?.currentValue ?? null,
    card.thresholds,
    card.trendDirection
  );

  const cardStyle = getCardStyle(thresholdStatus, card.color);

  // Determine if trend is good or bad
  const isTrendGood = () => {
    // v3.95 - UX-KPI-02: suppress trend chip when card has no value
    // == null catches both null and undefined; 0.0 == null is false so
    // zero-value cards (e.g. Total Weight = 0) are unaffected
    if (kpiValue?.currentValue == null) return null;
    if (!kpiValue?.changeDirection) return null;
    if (kpiValue.changeDirection === 'flat') return null;

    const isUp = kpiValue.changeDirection === 'up';
    const upIsGood = card.trendDirection === 'up_is_good';
    return isUp === upIsGood;
  };

  const trendIsGood = isTrendGood();

  const getTrendIcon = () => {
    if (!kpiValue?.changeDirection) return null;
    switch (kpiValue.changeDirection) {
      case 'up':
        return <TrendingUpIcon fontSize="small" />;
      case 'down':
        return <TrendingDownIcon fontSize="small" />;
      default:
        return <TrendingFlatIcon fontSize="small" />;
    }
  };

  const getTrendColor = () => {
    if (trendIsGood === null) return theme.palette.text.secondary;
    return trendIsGood ? theme.palette.success.main : theme.palette.error.main;
  };

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 3
        } : {},
        ...cardStyle
      }}
      onClick={onClick}
    >
      <CardContent>
        {/* Header with icon and title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: `${card.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: card.color
            }}
          >
            {getIcon(card.icon)}
          </Box>
          <Box sx={{ ml: 2, flex: 1 }}>
            {/* v3.29 - Allow title to wrap to 2 lines (remove noWrap truncation) */}
            <Typography variant="body2" color="text.secondary" sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.3,
            }}>
              {card.title}
            </Typography>
            {/* v3.29 - Allow description to wrap to 2 lines (remove noWrap truncation) */}
            {card.description && (
              <Typography variant="caption" color="text.secondary" sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mt: 0.25,
                lineHeight: 1.2,
              }}>
                {card.description}
              </Typography>
            )}
          </Box>
          {thresholdStatus !== 'normal' && (
            <Chip
              size="small"
              label={thresholdStatus === 'critical' ? 'Critical' : 'Warning'}
              color={thresholdStatus === 'critical' ? 'error' : 'warning'}
              sx={{ ml: 1 }}
            />
          )}
          {/* v4.10 - TRUST-KPI-01: per-card answer confidence (null when flag off
              or coverage gate fails — no visual change in that case) */}
          <Box sx={{ ml: 1 }}>
            <ConfidenceBadge confidence={kpiValue?.answer_confidence} />
          </Box>
        </Box>

        {/* Main value — v3.50: responsive clamp so "$1.64K" never clips at narrow widths */}
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: 'clamp(1.5rem, 4vw, 2.5rem)' }}>
          {formatValue(kpiValue?.currentValue ?? null, card.format, card.formatOptions, card.unit)}
        </Typography>

        {/* v3.26 - "vs previous" comparison for context */}
        {kpiValue?.previousValue != null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            vs {formatValue(kpiValue.previousValue, card.format, card.formatOptions, card.unit)} prev
          </Typography>
        )}

        {/* Sparkline with period labels */}
        {kpiValue?.sparkline && kpiValue.sparkline.length > 0 && (
          <Box sx={{ my: 2 }}>
            <Sparklines
              data={kpiValue.sparkline}
              width={200}
              height={40}
              margin={5}
            >
              <SparklinesLine
                color={card.color}
                style={{ strokeWidth: 2, fill: 'none' }}
              />
            </Sparklines>
            {kpiValue.sparklinePeriods && kpiValue.sparklinePeriods.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                {kpiValue.sparklinePeriods.map((period, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    {period}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Trend indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {kpiValue?.changePercent !== null && kpiValue?.changePercent !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', color: getTrendColor() }}>
                {getTrendIcon()}
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  {kpiValue.changePercent > 0 ? '+' : ''}
                  {kpiValue.changePercent.toFixed(1)}%
                </Typography>
              </Box>
            )}
            {/* v3.26 - Trend context chip: "Good" or "Needs attention" */}
            {trendIsGood !== null && (
              <Chip
                size="small"
                label={trendIsGood ? 'Good' : 'Needs attention'}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: trendIsGood ? 'success.light' : 'warning.light',
                  color: trendIsGood ? 'success.dark' : 'warning.dark',
                }}
              />
            )}
          </Box>
          {kpiValue?.asOfDate && (
            <Typography variant="caption" color="text.secondary">
              as of {new Date(kpiValue.asOfDate).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        {/* Error state */}
        {kpiValue?.error && (
          <Alert severity="error" sx={{ mt: 2, py: 0 }}>
            <Typography variant="caption">{kpiValue.error}</Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// v2.45 - Empty state component
const EmptyState: React.FC<{ onDataSourcesClick?: () => void }> = ({ onDataSourcesClick }) => (
  <Box
    sx={{
      textAlign: 'center',
      py: 6,
      px: 3,
      bgcolor: 'background.paper',
      borderRadius: 2,
      border: '1px dashed',
      borderColor: 'divider'
    }}
  >
    <BarChartIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      No KPIs Available
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Your dashboard will populate after your data pipeline completes.
    </Typography>
    {onDataSourcesClick && (
      <Button variant="outlined" onClick={onDataSourcesClick}>
        Check Data Sources
      </Button>
    )}
  </Box>
);

// v2.45 - Stale cache banner component
const StaleBanner: React.FC<{ computedAt: string }> = ({ computedAt }) => (
  <Alert severity="warning" sx={{ mb: 3 }}>
    <AlertTitle>Data may be outdated</AlertTitle>
    Data last updated {formatRelativeTime(computedAt)}. Your next pipeline run will refresh these metrics.
  </Alert>
);

// v2.45 - Error banner component
const ErrorBanner: React.FC<{ error: string; onRetry?: () => void }> = ({ error, onRetry }) => (
  <Alert
    severity="error"
    sx={{ mb: 3 }}
    action={
      onRetry && (
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      )
    }
  >
    <AlertTitle>Unable to load KPIs</AlertTitle>
    {error}
  </Alert>
);

// v2.45 - Main KPICardGrid component
export interface KPICardGridProps {
  onCardClick?: (cardId: string, linkedDashboard?: string) => void;
  onDataSourcesClick?: () => void;
}

const KPICardGrid: React.FC<KPICardGridProps> = ({
  onCardClick,
  onDataSourcesClick
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpisResponse, setKpisResponse] = useState<DashboardKPIsResponse | null>(null);
  const [cardsResponse, setCardsResponse] = useState<DashboardCardsResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch both KPIs and card definitions
  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const [kpis, cards] = await Promise.all([
        dashboardKPIService.getKPIs(),
        dashboardKPIService.getCards()
      ]);

      setKpisResponse(kpis);
      setCardsResponse(cards);
    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.userMessage || err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh handler
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(false);
  };

  // Loading state
  if (loading) {
    return <KPICardSkeleton count={4} />;
  }

  // Error state
  if (error) {
    return <ErrorBanner error={error} onRetry={handleRefresh} />;
  }

  // Empty state (no KPIs computed yet)
  const kpis = kpisResponse?.kpis || {};
  const cards = cardsResponse?.cards || [];

  if (cards.length === 0 || Object.keys(kpis).length === 0) {
    return <EmptyState onDataSourcesClick={onDataSourcesClick} />;
  }

  // Sort cards by position
  const sortedCards = [...cards].sort((a, b) => a.position - b.position);

  // Map cards to KPI values by cardId
  const getKpiForCard = (cardId: string): KPIValue | undefined => {
    // Find the KPI value that matches this cardId
    return Object.values(kpis).find(kpi => kpi.cardId === cardId);
  };

  const isStale = kpisResponse?.cacheStatus === 'stale';

  return (
    <Box>
      {/* Header with refresh button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Key Metrics
        </Typography>
        <Tooltip title="Refresh data">
          <IconButton onClick={handleRefresh} disabled={refreshing} aria-label="Refresh KPI data">
            <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stale cache warning */}
      {isStale && kpisResponse?.computedAt && (
        <StaleBanner computedAt={kpisResponse.computedAt} />
      )}

      {/* KPI Cards Grid */}
      <Grid container spacing={3}>
        {sortedCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.cardId}>
            <KPICard
              card={card}
              kpiValue={getKpiForCard(card.cardId)}
              onClick={onCardClick ? () => onCardClick(card.cardId) : undefined}
            />
          </Grid>
        ))}
      </Grid>

      {/* CSS for refresh animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default KPICardGrid;
